import React, { useState } from 'react';
import { ArrowRight, User, Briefcase, X } from 'lucide-react';
import { useAudience } from '@/context/AudienceContext';

/**
 * Un-missable hero-level persona prompt.
 *  - When in B2B mode (default): loud red-outlined banner nudging retail customers
 *    to switch to their view.
 *  - When in Retail mode: subtle inverse strip so partners can flip back.
 *  - Dismissible per-session so we don't nag returning visitors.
 */
export default function AudienceNudge() {
  const { audience, setAudience } = useAudience();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('mi_audience_nudge_dismissed') === '1'; } catch { return false; }
  });

  const dismiss = () => {
    try { sessionStorage.setItem('mi_audience_nudge_dismissed', '1'); } catch {}
    setDismissed(true);
  };

  if (dismissed) return null;

  // ── B2B → nudge retail customers ────────────────────────────────
  if (audience === 'b2b') {
    return (
      <div
        data-testid="audience-nudge-b2b"
        className="relative mb-8 group"
      >
        {/* red pulsing ring on the left edge */}
        <span
          aria-hidden="true"
          className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#E63946] animate-ping opacity-60"
        />
        <span
          aria-hidden="true"
          className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#E63946]"
        />

        <div className="border-2 border-[#E63946] bg-[#E63946]/[0.08] backdrop-blur-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-4 px-5 md:px-6 py-4">
            <div className="flex-shrink-0 hidden md:flex items-center justify-center w-11 h-11 border-2 border-[#E63946] bg-black text-[#E63946]">
              <User className="w-5 h-5" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="mi-eyebrow text-[#E63946] text-[10px] mb-1">// NOT A SHOP OWNER?</div>
              <div className="mi-headline text-xl md:text-2xl text-white leading-tight">
                Looking to wrap <span className="text-[#E63946]">your own vehicle</span>?
              </div>
              <div className="text-white/70 text-xs md:text-sm mt-1">
                We work direct with retail customers too — click here to see pricing, portfolio and quote form for personal vehicles.
              </div>
            </div>

            <button
              onClick={() => setAudience('retail')}
              data-testid="audience-nudge-switch-retail"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-[11px] md:text-xs px-5 py-3 hover:bg-[#E63946] hover:text-white transition-colors group/btn"
            >
              <User className="w-4 h-4" />
              I'm a retail customer
              <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
            </button>

            <button
              onClick={dismiss}
              data-testid="audience-nudge-dismiss"
              aria-label="Dismiss"
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Retail → smaller reciprocal nudge for partners ──────────────
  return (
    <div data-testid="audience-nudge-retail" className="relative mb-8">
      <div className="border mi-line bg-black/60 backdrop-blur-sm">
        <div className="flex items-center gap-3 px-4 py-2.5">
          <Briefcase className="w-4 h-4 text-white/50 flex-shrink-0" />
          <div className="flex-1 text-xs md:text-sm text-white/70">
            <span className="text-white/50">Shop owner or fleet manager?</span>{' '}
            <button
              onClick={() => setAudience('b2b')}
              data-testid="audience-nudge-switch-b2b"
              className="text-[#E63946] font-bold uppercase tracking-widest text-[11px] hover:underline"
            >
              See wholesale rates →
            </button>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
